import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeart,
  faShoppingCart,
  faTrash,
  faSpinner,
  faArrowLeft,
  faShare,
  faBoxOpen
} from '@fortawesome/free-solid-svg-icons';
import { wishlistAPI, cartAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useNotifications } from '../context/NotificationContext';
import Navbar from './Navbar';
import '../assets/css/wishlist.css';

const Wishlist = () => {
  const navigate = useNavigate();
  const { refreshCart } = useCart();
  const { showCustomToast } = useNotifications();

  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const response = await wishlistAPI.getWishlist();
      if (response && response.data) {
        setWishlistItems(response.data.items || response.data || []);
      }
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to load wishlist'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (itemId) => {
    try {
      setActionLoading(itemId);
      await wishlistAPI.removeFromWishlist(itemId);

      // Remove from local state
      setWishlistItems(prev => prev.filter(item => (item.product_id || item.product?._id || item.product?.id || item._id || item.id) !== itemId));

      showCustomToast({
        type: 'success',
        title: 'Removed',
        message: 'Item removed from wishlist'
      });
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to remove item'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddToCart = async (item) => {
    const productId = item.product_id || item.product?._id || item.product?.id || item._id || item.id;
    try {
      setActionLoading(productId);

      await cartAPI.addItem(productId, 1);
      await refreshCart();

      showCustomToast({
        type: 'success',
        title: 'Added to Cart',
        message: 'Item added to cart successfully'
      });
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to add to cart'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleMoveToCart = async (item) => {
    const productId = item.product_id || item.product?._id || item.product?.id || item._id || item.id;
    try {
      setActionLoading(productId);
      await wishlistAPI.moveToCart(productId);

      // Remove from local state
      setWishlistItems(prev => prev.filter(i => (i.product_id || i.product?._id || i.product?.id || i._id || i.id) !== productId));
      await refreshCart();

      showCustomToast({
        type: 'success',
        title: 'Moved to Cart',
        message: 'Item moved to cart successfully'
      });
    } catch (error) {
      // Fallback to add to cart
      await handleAddToCart(item);
    } finally {
      setActionLoading(null);
    }
  };

  const handleShareWishlist = async () => {
    try {
      const response = await wishlistAPI.shareWishlist();

      // Handle different response structures from backend
      const shareLink = response?.data?.shareLink ||
                       response?.data?.share_link ||
                       response?.data?.link ||
                       response?.data?.url;

      if (shareLink) {
        // Copy to clipboard
        await navigator.clipboard.writeText(shareLink);
        showCustomToast({
          type: 'success',
          title: 'Wishlist Shared',
          message: 'Share link copied to clipboard!'
        });
      } else {
        showCustomToast({
          type: 'success',
          title: 'Wishlist Shared',
          message: 'Your wishlist has been shared successfully'
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to share wishlist';
      showCustomToast({
        type: 'error',
        title: 'Share Failed',
        message: errorMessage
      });
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(price || 0);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="wishlist-container">
          <div className="wishlist-loading">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            <p>Loading wishlist...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="wishlist-container">
        <div className="wishlist-header">
          <button onClick={() => navigate(-1)} className="wishlist-back-btn">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className="wishlist-title">
            <FontAwesomeIcon icon={faHeart} />
            <h1>My Wishlist</h1>
            <span className="wishlist-count">{wishlistItems.length} items</span>
          </div>
          {wishlistItems.length > 0 && (
            <button onClick={handleShareWishlist} className="wishlist-share-btn">
              <FontAwesomeIcon icon={faShare} />
              Share
            </button>
          )}
        </div>

        {wishlistItems.length === 0 ? (
          <div className="wishlist-empty">
            <FontAwesomeIcon icon={faBoxOpen} size="4x" />
            <h2>Your wishlist is empty</h2>
            <p>Start adding items you love to your wishlist</p>
            <button onClick={() => navigate('/shop')} className="wishlist-shop-btn">
              Browse Products
            </button>
          </div>
        ) : (
          <div className="wishlist-grid">
            {wishlistItems.map((item) => {
              const product = item.product || item;
              // The wishlist API returns one row per product, but every row's
              // top-level _id used to be the shared WISHLIST document's own
              // id (identical across every item) — using that here broke
              // remove/move-to-cart (backend matches on the product id) and
              // gave React duplicate keys. product_id (added server-side) is
              // the real, per-item identifier both actions actually need.
              const itemId = item.product_id || product._id || product.id || item._id || item.id;
              const productImage = product.images?.[0] || product.image || 'https://via.placeholder.com/300';
              const isLoading = actionLoading === itemId;

              return (
                <div key={itemId} className="wishlist-item">
                  <div
                    className="wishlist-item-image"
                    onClick={() => navigate(`/product/${product._id || product.id}`)}
                  >
                    <img src={productImage} alt={product.title || product.name} />
                    {product.stock === 0 && (
                      <div className="wishlist-out-of-stock">Out of Stock</div>
                    )}
                  </div>

                  <div className="wishlist-item-details">
                    <h3 onClick={() => navigate(`/product/${product._id || product.id}`)}>
                      {product.title || product.name}
                    </h3>
                    <p className="wishlist-item-price">{formatPrice(product.price)}</p>

                    {product.stock > 0 && (
                      <p className="wishlist-item-stock">
                        {product.stock} in stock
                      </p>
                    )}

                    <div className="wishlist-item-actions">
                      <button
                        onClick={() => handleMoveToCart(item)}
                        className="wishlist-btn wishlist-btn-primary"
                        disabled={isLoading || product.stock === 0}
                      >
                        {isLoading ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faShoppingCart} />
                            Add to Cart
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => handleRemoveFromWishlist(itemId)}
                        className="wishlist-btn wishlist-btn-remove"
                        disabled={isLoading}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>

                    {item.added_at && (
                      <p className="wishlist-item-date">
                        Added {new Date(item.added_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default Wishlist;
